import { Input } from '@etg/wings';
import { useController, useFormContext } from 'react-hook-form';
import type { ChangeEvent, FocusEvent, KeyboardEvent } from 'react';
import { forwardRef } from 'react';
import { useTranslation } from '@eti/providers';
import type { DateFormat } from '../types/dateFormat';

export interface DateInputNewProps {
  'aria-label'?: string;
  name: string;
  dateFormat: DateFormat;
  formatDate?: (value: Date, dateFormat: DateFormat) => string;
  onBlur?: (event: FocusEvent<HTMLInputElement>, name: string) => void;
  onChange?: (event: ChangeEvent<HTMLInputElement>, name: string) => void;
  onFocus?: () => void;
  placeholder: string;
}

const DateInputNew = forwardRef<HTMLInputElement, DateInputNewProps>(
  (
    {
      'aria-label': ariaLabel,
      name,
      dateFormat,
      formatDate,
      onBlur,
      onChange,
      onFocus,
      placeholder,
    },
    ref,
  ) => {
    const { control, trigger } = useFormContext();
    const { t } = useTranslation();
    const { field, fieldState } = useController({
      control,
      name,
      rules: { required: t('Validation.Required') },
    });

    const handleOnBlur = (event: FocusEvent<HTMLInputElement>) => {
      onBlur?.(event, name);
    };

    const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
      onChange?.(event, name);
    };

    const handleOnFocus = () => {
      onFocus?.();
      trigger(name);
    };

    const handleOnKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault();
      }
    };

    return (
      <Input
        aria-label={ariaLabel}
        autoComplete="off"
        data-testid={`${name}-input`}
        defaultValue={formatDate?.(field.value, dateFormat)}
        hasError={fieldState.error && !field.value}
        id={name}
        inputMode="none"
        name={name}
        onBlur={handleOnBlur}
        onChange={handleOnChange}
        onFocus={handleOnFocus}
        onKeyDown={handleOnKeyDown}
        placeholder={placeholder}
        ref={ref}
      />
    );
  },
);

export default DateInputNew;
